<script setup>
import { MessageBubble } from '@dashhudson/dashing-ui/message-bubble';
import { decamelizeKeys } from 'humps';
import { computed } from 'vue';

import MessageText from '@/app/community/components/MessageText.vue';
import { useInteractionMenu } from '@/app/community/composables/useInteractionMenu';
import {
  COMMUNITY_ASSIGNMENT_COMMENT_TEXT,
  COMMUNITY_COMMENT_RESOURCE_TYPES,
  FAILED_RULE_DELETE_MESSAGE,
  FAILED_RULE_HIDE_MESSAGE,
  FAILED_RULE_SEND_DM_MESSAGE,
} from '@/app/community/constants';
import { RULE_ACTION_KEYS, RULE_ACTION_TEXT_MAP } from '@/app/community/constants/inboxAutomation';
import { getInteractionGenericType } from '@/app/community/utils/community-interactions';
import { useCommunityStore } from '@/stores/community';
import { decodeSpansFromText, getSpanText } from '@/utils/commenting';

const communityStore = useCommunityStore();

const props = defineProps({
  message: { type: Object, default: () => ({}) },
  comment: { type: Object, default: () => ({}) },
});

const target = computed(() => ({ ...props.comment, message: props.message }));
const { internalMessageMenu } = useInteractionMenu(target);

const menuItems = computed(() => internalMessageMenu.value?.menuItems ?? []);
const numMenuButtons = computed(() => internalMessageMenu.value?.numMenuButtons ?? []);
const referencedUsers = computed(() =>
  decamelizeKeys(communityStore.internalMessagesReferencedUsers ?? {}),
);
const messageText = computed(() => {
  if (props.message.resourceType === COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_RULE_EXECUTED) {
    const action = props.message.action;

    const actionKey = Object.keys(action || {}).find(
      (key) => action[key] && RULE_ACTION_TEXT_MAP[key],
    );
    if (actionKey) {
      const actionText = RULE_ACTION_TEXT_MAP[actionKey];
      const isReplyAction =
        actionKey === RULE_ACTION_KEYS.PRESET_REPLY_ID ||
        actionKey === RULE_ACTION_KEYS.PRIVATE_REPLY_PRESET_REPLY_ID;
      return isReplyAction
        ? `Automation Rule Executed. ${actionText}.`
        : `Automation Rule Executed. Message ${actionText}.`;
    }
    return `Automation Rule Executed: ${props.message.text}`;
  }
  if (
    props.message.resourceType === COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_RULE_DELETE_FAILED
  ) {
    return FAILED_RULE_DELETE_MESSAGE;
  }
  if (props.message.resourceType === COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_RULE_HIDE_FAILED) {
    return FAILED_RULE_HIDE_MESSAGE;
  }
  if (
    props.message.resourceType === COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_RULE_SEND_DM_FAILED
  ) {
    return FAILED_RULE_SEND_DM_MESSAGE;
  }
  if (
    [
      COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_ASSIGNMENT,
      COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_INTERNAL_NOTE,
    ].includes(props.message.resourceType)
  ) {
    const isAssignment =
      props.message.resourceType === COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_ASSIGNMENT;
    const interactionType = getInteractionGenericType(props.comment.type);
    const baseText = isAssignment
      ? `${interactionType} assigned to ${props.message.text}`
      : props.message.text;
    if (props.message.text.match(/@\[\d+\]/)) {
      const textSpans = decodeSpansFromText(baseText, referencedUsers.value);
      return textSpans.map((span) => getSpanText(span, isAssignment)).join('');
    }
    if (props.message.text === COMMUNITY_ASSIGNMENT_COMMENT_TEXT.UNASSIGNED) {
      return `${interactionType} unassigned`;
    }
  }
  return props.message.text;
});
const messageTextClass = computed(() => {
  if (props.message.resourceType === COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_RULE_EXECUTED)
    return 'dash-text-knockout';
  if (
    [
      COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_RULE_DELETE_FAILED,
      COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_RULE_HIDE_FAILED,
      COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_RULE_SEND_DM_FAILED,
    ].includes(props.message.resourceType)
  )
    return 'dash-text-subtle';
  return props.message.resourceType === COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_ASSIGNMENT
    ? 'dash-text-subtle'
    : 'dash-text-primary';
});

const formattedMessage = computed(() => {
  const { orientation, variant, avatar, date, menuProps, errorTooltip, resourceType } =
    props.message ?? {};
  return {
    orientation,
    variant,
    avatar,
    date,
    menuProps,
    errorTooltip,
    resourceType,
  };
});
</script>

<template>
  <MessageBubble
    v-bind="formattedMessage"
    :menu-items="menuItems"
    :num-menu-buttons="numMenuButtons"
  >
    <MessageText :text="messageText" :class="['fs-mask', '!break-all', messageTextClass]" />
  </MessageBubble>
</template>
